import React, { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'
import adminService from '../../services/adminService'
import PageTitle from '../../components/Common/PageTitle'
import Skeleton from 'react-loading-skeleton'

const AdminAuditLogs = () => {
  const [page, setPage] = useState(1)
  const [data, setData] = useState({ logs: [], totalCount: 0, totalPages: 0, hasNextPage: false, hasPrevPage: false })
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const result = await adminService.getAdminAuditLogs({ page, limit: 50 })
      setData(result)
    } catch (e) {
      toast.error('Failed to load audit logs')
    } finally {
      setLoading(false)
    }
  }, [page])

  useEffect(() => { load() }, [load])

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <PageTitle title="Audit Logs" />
        <button onClick={load} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm">
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border">
        {loading ? (
          <div className="p-6">
            {Array(8).fill(0).map((_, i) => (
              <Skeleton key={i} height={32} className="mb-2" />
            ))}
          </div>
        ) : data.logs.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No admin actions recorded yet.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">When</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.logs.map(log => (
                <React.Fragment key={log.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-sm">
                      <div className="font-medium text-gray-900">{log.users?.name || '—'}</div>
                      <div className="text-xs text-gray-500">{log.users?.email || ''}</div>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-700">
                      {String(log.action).replace(/^admin_/, '').replace(/_/g, ' ')}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-500">
                      {log.resource_type}
                      {log.resource_id ? <span className="text-xs text-gray-400 block">{log.resource_id}</span> : null}
                    </td>
                    <td className="px-6 py-3 text-sm">
                      <button
                        onClick={() => setExpanded(prev => prev === log.id ? null : log.id)}
                        className="text-blue-600 hover:text-blue-800 text-xs"
                      >
                        {expanded === log.id ? 'Hide' : 'Show'}
                      </button>
                    </td>
                  </tr>
                  {expanded === log.id && (
                    <tr className="bg-gray-50">
                      <td colSpan={5} className="px-6 py-3">
                        <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}

        {data.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <p className="text-sm text-gray-700">
              Page <span className="font-medium">{page}</span> of{' '}
              <span className="font-medium">{data.totalPages}</span> · {data.totalCount} entries
            </p>
            <div className="space-x-2">
              <button
                disabled={!data.hasPrevPage}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                disabled={!data.hasNextPage}
                onClick={() => setPage(p => p + 1)}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminAuditLogs
